import React, { useState, useEffect } from 'react';
import { X, Download, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FilePreviewModal = ({ isOpen, onClose, file, downloadUrl }) => {
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setLoading(true);
      setError(null);
    }
  }, [isOpen, file]);

  if (!isOpen || !file) return null;

  const isImage = file.mime_type?.startsWith('image/');
  const isPDF = file.mime_type === 'application/pdf';

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="relative w-full h-full max-w-7xl max-h-[90vh] m-4 bg-white rounded-lg shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-slate-50">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900 truncate">{file.filename}</h3>
            <p className="text-sm text-slate-600">
              {file.mime_type} • {formatFileSize(file.size)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(isImage || isPDF) && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleZoomOut}
                  disabled={scale <= 0.5}
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium px-2">{Math.round(scale * 100)}%</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleZoomIn}
                  disabled={scale >= 3}
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="ml-2"
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="ml-2"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-slate-100 p-4">
          {loading && (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#247C75]"></div>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={handleDownload} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Download Instead
              </Button>
            </div>
          )}

          {isImage && (
            <div className="flex items-center justify-center min-h-full">
              <img
                src={downloadUrl}
                alt={file.filename}
                style={{ transform: `scale(${scale})`, transition: 'transform 0.2s' }}
                className="max-w-full h-auto object-contain"
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false);
                  setError('Failed to load image');
                }}
              />
            </div>
          )}

          {isPDF && (
            <div className="flex items-center justify-center h-full">
              <iframe
                src={`${downloadUrl}#view=FitH`}
                title={file.filename}
                className="w-full h-full border-0 rounded"
                style={{ 
                  transform: `scale(${scale})`,
                  transformOrigin: 'top center',
                  minHeight: '600px'
                }}
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false);
                  setError('Failed to load PDF. Your browser may not support PDF preview.');
                }}
              />
            </div>
          )}

          {!isImage && !isPDF && (
            <div className="flex flex-col items-center justify-center h-full">
              <p className="text-slate-600 mb-4">Preview not available for this file type</p>
              <Button onClick={handleDownload} className="bg-[#247C75] hover:bg-[#1d6259]">
                <Download className="h-4 w-4 mr-2" />
                Download File
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export default FilePreviewModal;
